import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject, catchError, distinctUntilChanged, map, of, takeUntil } from 'rxjs';
import { IdeasApiService } from '../../shared/api/ideas-api.service';
import {
  IdeaCommentDto,
  IdeaDeliverableAttachmentDto,
  IdeaDto,
  IdeaHelperDto,
  IdeaTimelineItemDto
} from '../../shared/api/ideas-api.types';
import { AuthService } from '../../shared/auth/auth.service';
import { LoaderComponent } from '../../shared/ui/loader/loader.component';

@Component({
  selector: 'app-idea-details',
  standalone: true,
  imports: [CommonModule, FormsModule, LoaderComponent],
  templateUrl: './idea-details.component.html',
  styleUrl: './idea-details.component.css'
})
export class IdeaDetailsComponent implements OnInit, OnDestroy {
  loading = true;
  errorMessage = '';
  idea: IdeaDto | null = null;

  comments: IdeaCommentDto[] = [];
  commentRows: { comment: IdeaCommentDto; depth: number }[] = [];
  commentsLoading = false;
  commentComposerOpen = true;
  commentBody = '';
  savingVote = false;
  postingComment = false;
  replyingToCommentId: string | null = null;
  replyBody = '';
  postingReply = false;
  following = false;
  helping = false;
  editingCommentId: string | null = null;
  editCommentBody = '';
  updatingCommentId: string | null = null;
  deletingCommentId: string | null = null;
  commentPendingDelete: IdeaCommentDto | null = null;
  helpConfirmationOpen = false;

  timelineItems: IdeaTimelineItemDto[] = [];
  timelineDisplay: { title: string; atIso: string }[] = [];
  timelineLoading = false;
  downloadingAttachmentId: string | null = null;
  deliverableError = '';

  private readonly destroy$ = new Subject<void>();
  private ideaId: string | null = null;

  constructor(
    private readonly location: Location,
    private readonly route: ActivatedRoute,
    private readonly api: IdeasApiService,
    private readonly auth: AuthService
  ) {}

  goBack(): void {
    this.location.back();
  }

  get isAuthenticated(): boolean {
    return !!this.auth.currentUser?.userId;
  }

  ngOnInit(): void {
    this.auth.refreshCurrentUser().pipe(takeUntil(this.destroy$)).subscribe();
    this.route.paramMap
      .pipe(
        map((params) => params.get('id')),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((id) => {
        this.handleIdeaRouteChange(id);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadTimeline(requestIdeaId = this.ideaId): void {
    if (!requestIdeaId) return;

    this.timelineLoading = true;
    this.api
      .getIdeaTimeline(requestIdeaId)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of<IdeaTimelineItemDto[]>([]))
      )
      .subscribe({
        next: (items) => {
          if (this.ideaId !== requestIdeaId) {
            return;
          }
          this.timelineItems = items ?? [];
          this.rebuildTimelineDisplay();
          this.timelineLoading = false;
        }
      });
  }

  private rebuildTimelineDisplay(): void {
    const idea = this.idea;
    if (!idea) {
      this.timelineDisplay = [];
      return;
    }

    const items = [...this.timelineItems].sort(
      (a, b) => new Date(a.changedAt).getTime() - new Date(b.changedAt).getTime()
    );
    const created = idea.createdAtUtc ?? '';
    const out: { title: string; atIso: string }[] = [];
    const isDraft = this.isDraftIdeaStatus(idea.status);

    if (items.length === 0) {
      out.push({
        title: isDraft ? 'Draft saved' : 'Submitted',
        atIso: created
      });
      this.timelineDisplay = out;
      return;
    }

    let startIndex = 0;
    const first = items[0];

    if (first.fromStatus === 'Draft' && first.toStatus === 'Submitted') {
      out.push({ title: 'Submitted', atIso: first.changedAt });
      startIndex = 1;
    } else if (created && !isDraft) {
      out.push({ title: 'Submitted', atIso: created });
    }

    for (let i = startIndex; i < items.length; i++) {
      const item = items[i];
      if (item.fromStatus === 'Draft' && item.toStatus === 'Submitted') {
        continue;
      }
      out.push({
        title: `Moved to ${this.formatTimelineStatus(item.toStatus)}`,
        atIso: item.changedAt
      });
    }

    this.timelineDisplay = out;
  }

  private isDraftIdeaStatus(status: IdeaDto['status']): boolean {
    if (typeof status === 'number') {
      return status === 0;
    }
    return String(status).toLowerCase() === 'draft';
  }

  formatTimelineStatus(apiStatus: string): string {
    const map: Record<string, string> = {
      Draft: 'Draft',
      Submitted: 'Submitted',
      Triage: 'Triage',
      UnderReview: 'Under Review',
      NeedMoreInfo: 'Need More Info',
      Accepted: 'Accepted',
      Rejected: 'Rejected',
      Planned: 'Planned',
      InProgress: 'In Progress',
      Delivered: 'Delivered',
      Archived: 'Archived'
    };
    const key = (apiStatus ?? '').trim();
    if (map[key]) {
      return map[key];
    }
    return key.replace(/([a-z])([A-Z])/g, '$1 $2').trim() || key;
  }

  loadComments(requestIdeaId = this.ideaId): void {
    if (!requestIdeaId) return;

    this.commentsLoading = true;
    this.api
      .getComments(requestIdeaId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items) => {
          if (this.ideaId !== requestIdeaId) {
            return;
          }
          this.comments = items ?? [];
          this.rebuildCommentRows();
          this.commentsLoading = false;
        },
        error: () => {
          if (this.ideaId !== requestIdeaId) {
            return;
          }
          this.commentsLoading = false;
        }
      });
  }

  private handleIdeaRouteChange(id: string | null): void {
    this.resetIdeaViewState();

    if (!id) {
      this.loading = false;
      this.errorMessage = 'Missing idea id.';
      return;
    }

    this.ideaId = id;
    this.api
      .getIdeaById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (idea) => {
          if (this.ideaId !== id) {
            return;
          }
          this.idea = idea;
          this.loading = false;
          this.loadComments(id);
          this.loadTimeline(id);
        },
        error: () => {
          if (this.ideaId !== id) {
            return;
          }
          this.loading = false;
          this.errorMessage = 'Failed to load idea.';
        }
      });
  }

  private resetIdeaViewState(): void {
    this.loading = true;
    this.errorMessage = '';
    this.idea = null;
    this.comments = [];
    this.commentRows = [];
    this.commentsLoading = false;
    this.commentComposerOpen = true;
    this.commentBody = '';
    this.replyingToCommentId = null;
    this.replyBody = '';
    this.postingReply = false;
    this.editingCommentId = null;
    this.editCommentBody = '';
    this.updatingCommentId = null;
    this.deletingCommentId = null;
    this.commentPendingDelete = null;
    this.helpConfirmationOpen = false;
    this.timelineItems = [];
    this.timelineDisplay = [];
    this.timelineLoading = false;
    this.downloadingAttachmentId = null;
    this.deliverableError = '';
  }

  private rebuildCommentRows(): void {
    this.commentRows = this.flattenComments(this.comments);
  }

  private flattenComments(items: IdeaCommentDto[]): { comment: IdeaCommentDto; depth: number }[] {
    type Node = IdeaCommentDto & { _replies: Node[] };

    const byId = new Map<string, Node>();
    for (const c of items ?? []) {
      byId.set(String(c.id), { ...c, _replies: [] } as Node);
    }

    const roots: Node[] = [];
    for (const c of byId.values()) {
      const parentId = (c.parentCommentId ?? '').trim();
      if (parentId && byId.has(parentId)) {
        byId.get(parentId)!._replies.push(c as Node);
      } else {
        roots.push(c);
      }
    }

    const parseTime = (iso?: string | null) => {
      const t = new Date(iso ?? '').getTime();
      return Number.isFinite(t) ? t : 0;
    };

    // Keep newest threads at the top; keep replies in chronological order.
    roots.sort((a, b) => parseTime(b.createdAtUtc) - parseTime(a.createdAtUtc));
    const sortReplies = (list: Node[]) => {
      list.sort((a, b) => parseTime(a.createdAtUtc) - parseTime(b.createdAtUtc));
      for (const x of list) {
        if (x._replies?.length) {
          sortReplies(x._replies);
        }
      }
    };
    for (const r of roots) {
      if (r._replies?.length) {
        sortReplies(r._replies);
      }
    }

    const out: { comment: IdeaCommentDto; depth: number }[] = [];
    const walk = (node: Node, depth: number) => {
      out.push({ comment: node, depth });
      for (const r of node._replies ?? []) {
        walk(r, Math.min(depth + 1, 8));
      }
    };
    for (const r of roots) {
      walk(r, 0);
    }
    return out;
  }

  toggleVote(): void {
    if (!this.ideaId || !this.idea || this.savingVote) return;

    this.savingVote = true;
    const hadUpvote = this.idea.hasVoted;
    const action$ = hadUpvote ? this.api.unvoteIdea(this.ideaId) : this.api.voteIdea(this.ideaId);

    action$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.refetchIdea(),
      error: () => {
        this.savingVote = false;
      }
    });
  }

  toggleDownvote(): void {
    if (!this.ideaId || !this.idea || this.savingVote) return;

    this.savingVote = true;
    const hadDownvote = this.idea.hasDownvoted ?? false;
    const action$ = hadDownvote ? this.api.undownvoteIdea(this.ideaId) : this.api.downvoteIdea(this.ideaId);

    action$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.refetchIdea(),
      error: () => {
        this.savingVote = false;
      }
    });
  }

  private refetchIdea(): void {
    if (!this.ideaId) {
      this.savingVote = false;
      return;
    }
    this.api
      .getIdeaById(this.ideaId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (idea) => {
          this.idea = idea;
          this.savingVote = false;
        },
        error: () => {
          this.savingVote = false;
        }
      });
  }

  postComment(): void {
    if (!this.ideaId || this.postingComment) return;

    const body = this.commentBody.trim();
    if (!body.length) return;

    this.postingComment = true;
    this.api
      .postComment(this.ideaId, body, null)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (created) => {
          this.comments = [created, ...this.comments];
          this.rebuildCommentRows();
          this.commentBody = '';
          this.commentComposerOpen = false;
          this.postingComment = false;
        },
        error: () => {
          this.postingComment = false;
        }
      });
  }

  openCommentComposer(): void {
    this.commentComposerOpen = true;
  }

  closeCommentComposer(): void {
    if (this.postingComment) return;
    this.commentComposerOpen = false;
    this.commentBody = '';
  }

  startReply(comment: IdeaCommentDto): void {
    if (this.postingReply || this.postingComment) return;
    this.replyingToCommentId = comment.id;
    this.replyBody = '';
  }

  cancelReply(): void {
    if (this.postingReply) return;
    this.replyingToCommentId = null;
    this.replyBody = '';
  }

  postReply(parentCommentId: string): void {
    if (!this.ideaId || this.postingReply) return;
    const body = this.replyBody.trim();
    if (!body.length) return;

    this.postingReply = true;
    this.api
      .postComment(this.ideaId, body, parentCommentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (created) => {
          this.comments = [created, ...this.comments];
          this.rebuildCommentRows();
          this.postingReply = false;
          this.cancelReply();
        },
        error: () => {
          this.postingReply = false;
        }
      });
  }

  isCommentOwner(comment: IdeaCommentDto): boolean {
    const uid = this.auth.currentUser?.userId;
    if (!uid || !comment.userId) {
      return false;
    }
    return uid.toLowerCase() === String(comment.userId).toLowerCase();
  }

  startEditComment(comment: IdeaCommentDto): void {
    this.editingCommentId = comment.id;
    this.editCommentBody = comment.body;
  }

  cancelEditComment(): void {
    this.editingCommentId = null;
    this.editCommentBody = '';
  }

  saveEditComment(): void {
    if (!this.ideaId || !this.editingCommentId || this.updatingCommentId) {
      return;
    }
    const body = this.editCommentBody.trim();
    if (!body.length) {
      return;
    }

    const id = this.editingCommentId;
    this.updatingCommentId = id;
    this.api
      .updateComment(this.ideaId, id, body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          this.comments = this.comments.map((c) => (c.id === updated.id ? updated : c));
          this.rebuildCommentRows();
          this.cancelEditComment();
          this.updatingCommentId = null;
        },
        error: () => {
          this.updatingCommentId = null;
        }
      });
  }

  openDeleteCommentDialog(comment: IdeaCommentDto): void {
    if (this.deletingCommentId || this.updatingCommentId) {
      return;
    }
    this.commentPendingDelete = comment;
  }

  closeDeleteCommentDialog(): void {
    if (this.deletingCommentId) {
      return;
    }
    this.commentPendingDelete = null;
  }

  confirmDeleteComment(): void {
    const comment = this.commentPendingDelete;
    if (!comment || !this.ideaId || this.deletingCommentId) {
      return;
    }

    this.deletingCommentId = comment.id;
    this.api
      .deleteComment(this.ideaId, comment.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Backend promotes direct replies to top-level when deleting a parent.
          this.comments = this.comments
            .filter((c) => c.id !== comment.id)
            .map((c) => (c.parentCommentId?.toLowerCase() === comment.id.toLowerCase() ? { ...c, parentCommentId: null } : c));
          this.rebuildCommentRows();
          if (this.editingCommentId === comment.id) {
            this.cancelEditComment();
          }
          this.deletingCommentId = null;
          this.commentPendingDelete = null;
        },
        error: () => {
          this.deletingCommentId = null;
        }
      });
  }

  @HostListener('document:keydown.escape')
  onEscapeCloseDeleteDialog(): void {
    if (this.commentPendingDelete) {
      this.closeDeleteCommentDialog();
      return;
    }
    if (this.helpConfirmationOpen) {
      this.closeHelpConfirmation();
    }
  }

  onHelpButtonClick(): void {
    if (!this.idea || this.helping) return;
    if (this.idea.isHelping) {
      this.toggleHelp();
      return;
    }
    this.helpConfirmationOpen = true;
  }

  closeHelpConfirmation(): void {
    if (this.helping) {
      return;
    }
    this.helpConfirmationOpen = false;
  }

  confirmHelp(): void {
    if (!this.helpConfirmationOpen) {
      return;
    }
    this.helpConfirmationOpen = false;
    this.toggleHelp();
  }

  toggleFollow(): void {
    if (!this.idea || this.following) return;
    this.following = true;
    const op = this.idea.isFollowing ? this.api.unfollowIdea(this.idea.id) : this.api.followIdea(this.idea.id);
    op
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.following = false;
        if (this.idea) {
          this.idea = { ...this.idea, isFollowing: !this.idea.isFollowing };
        }
      });
  }

  toggleHelp(): void {
    if (!this.idea || this.helping) return;
    this.helping = true;
    const op = this.idea.isHelping ? this.api.unhelpIdea(this.idea.id) : this.api.helpIdea(this.idea.id);
    op
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.helping = false;
        this.refetchIdea();
      });
  }

  getStatusLabel(status: IdeaDto['status']): string {
    if (typeof status === 'string') return status;
    const labels: Record<number, string> = {
      0: 'Draft',
      1: 'Submitted',
      2: 'Triage',
      3: 'Under Review',
      4: 'Accepted',
      5: 'Rejected',
      6: 'Planned',
      7: 'In Progress',
      8: 'Delivered',
      9: 'Archived',
      10: 'Need more info'
    };
    return labels[status] ?? 'Unknown';
  }

  trackTimelineRow(_index: number, row: { title: string; atIso: string }): string {
    return `${row.title}-${row.atIso}`;
  }

  getImpactScopeDisplay(scope: IdeaDto['impactScope']): string {
    const labels = this.getImpactScopeLabels(scope);
    return labels.length ? labels.join(', ') : '—';
  }

  getImpactScopeLabels(scope: IdeaDto['impactScope']): string[] {
    if (!scope) return [];
    const labels: Array<[boolean, string]> = [
      [scope.myTeam, 'My Team'],
      [scope.myDepartment, 'My Department'],
      [scope.companyWide, 'Company-wide'],
      [scope.customers, 'Customers'],
      [scope.product, 'Product']
    ];
    return labels.filter(([on]) => on).map(([, label]) => label);
  }

  getExpectedImpactLabels(type: IdeaDto['expectedImpactType']): string[] {
    if (!type) return [];
    const labels: Array<[boolean, string]> = [
      [type.timeSaved, 'Time saved'],
      [type.costSaved, 'Cost saved'],
      [type.revenue, 'Revenue'],
      [type.quality, 'Quality'],
      [type.riskReduction, 'Risk reduction'],
      [type.employeeExperience, 'Employee experience']
    ];
    return labels.filter(([on]) => on).map(([, label]) => label);
  }

  getUpvoteCount(): number {
    return Math.max(0, this.idea?.upvoteCount ?? this.idea?.voteCount ?? 0);
  }

  getDownvoteCount(): number {
    return Math.max(0, this.idea?.downvoteCount ?? 0);
  }

  getInitials(name: string): string {
    if (!name?.trim()) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  helperInitials(helper: IdeaHelperDto): string {
    return this.getInitials(helper.displayName ?? '');
  }

  deliverableAttachments(idea: IdeaDto): IdeaDeliverableAttachmentDto[] {
    return idea.deliverableAttachments ?? [];
  }

  hasDeliveredAttachments(idea: IdeaDto): boolean {
    return this.isDeliveredIdea(idea) || this.deliverableAttachments(idea).length > 0;
  }

  isDeliveredIdea(idea: IdeaDto): boolean {
    if (typeof idea.status === 'number') {
      return idea.status === 8;
    }
    return String(idea.status).trim().toLowerCase() === 'delivered';
  }

  downloadDeliverable(ideaId: string, attachment: IdeaDeliverableAttachmentDto): void {
    if (this.downloadingAttachmentId) {
      return;
    }

    this.downloadingAttachmentId = attachment.id;
    this.deliverableError = '';

    this.api
      .downloadIdeaDeliverable(ideaId, attachment.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = attachment.fileName;
          link.target = '_blank';
          link.rel = 'noopener';
          link.click();
          setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
          this.downloadingAttachmentId = null;
        },
        error: (err: unknown) => {
          this.downloadingAttachmentId = null;
          this.deliverableError = this.formatDeliverableError(err);
        }
      });
  }

  formatFileSize(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes <= 0) {
      return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    const precision = size >= 10 || unitIndex === 0 ? 0 : 1;
    return `${size.toFixed(precision)} ${units[unitIndex]}`;
  }

  isImageAttachment(attachment: IdeaDeliverableAttachmentDto): boolean {
    return attachment.contentType?.toLowerCase().startsWith('image/') ?? false;
  }

  formatDate(iso: string): string {
    if (!iso?.trim()) {
      return '—';
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return '—';
    }
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffSec < 60) return 'less than a minute ago';
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 365) return `about ${Math.floor(diffDays / 30)} months ago`;
    const years = Math.floor(diffDays / 365);
    return `over ${years} year${years > 1 ? 's' : ''} ago`;
  }

  private formatDeliverableError(err: unknown): string {
    if (!(err instanceof HttpErrorResponse)) {
      return 'Could not download the file.';
    }

    if (err.status === 404) {
      return 'This file is no longer available.';
    }

    if (err.status === 403) {
      return 'You are not allowed to download this file.';
    }

    return 'Could not download the file.';
  }
}
